(() => {
  class CollectionGridView {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('#product-grid, .product-grid');
      this.buttons = Array.from(root.querySelectorAll('[data-grid-view-mode]'));
      this.stages = [];
      this.defaultMode = this.grid?.dataset.gridDefaultMode || 'comfortable';
      this.mode = this.defaultMode;
      this.onButtonClick = this.onButtonClick.bind(this);
      this.onStagePointerEnter = this.onStagePointerEnter.bind(this);
      this.onStagePointerLeave = this.onStagePointerLeave.bind(this);
      this.onStageMouseEnter = this.onStageMouseEnter.bind(this);
      this.onStageMouseLeave = this.onStageMouseLeave.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this._sizingRaf1 = null;
      this._sizingRaf2 = null;
      this._sizingTimerShort = null;
      this._sizingTimerLong = null;
    }

    init() {
      if (!this.grid) return;

      if (this.buttons.length) {
        this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick));
        this.applyMode(this.mode);
      }

      this.bindStageHoverHandlers();
      this.applyFramedCardSizingForMode();
      this.schedulePostLayoutSizingPasses();
      window.addEventListener('resize', this.onWindowResize, { passive: true });
    }

    destroy() {
      this.buttons.forEach((button) => button.removeEventListener('click', this.onButtonClick));
      this.stages.forEach((stage) => {
        stage.removeEventListener('pointerenter', this.onStagePointerEnter);
        stage.removeEventListener('pointerleave', this.onStagePointerLeave);
        stage.removeEventListener('mouseenter', this.onStageMouseEnter);
        stage.removeEventListener('mouseleave', this.onStageMouseLeave);
        stage.classList.remove('is-stage-hovered');
      });
      this.stages = [];
      this.clearScheduledSizingPasses();
      window.removeEventListener('resize', this.onWindowResize);
    }

    onButtonClick(event) {
      const nextMode = event.currentTarget?.dataset?.gridViewMode;
      if (!nextMode || nextMode === this.mode) return;
      this.applyMode(nextMode);
    }

    applyMode(mode) {
      this.mode = mode;
      this.grid.classList.remove('collection-grid-mode--comfortable', 'collection-grid-mode--dense');
      this.grid.classList.add(`collection-grid-mode--${mode}`);

      this.buttons.forEach((button) => {
        const isActive = button.dataset.gridViewMode === mode;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.applyFramedCardSizingForMode();
      this.schedulePostLayoutSizingPasses();
    }

    clearScheduledSizingPasses() {
      if (this._sizingRaf1) cancelAnimationFrame(this._sizingRaf1);
      if (this._sizingRaf2) cancelAnimationFrame(this._sizingRaf2);
      if (this._sizingTimerShort) clearTimeout(this._sizingTimerShort);
      if (this._sizingTimerLong) clearTimeout(this._sizingTimerLong);
      this._sizingRaf1 = null;
      this._sizingRaf2 = null;
      this._sizingTimerShort = null;
      this._sizingTimerLong = null;
    }

    schedulePostLayoutSizingPasses() {
      this.clearScheduledSizingPasses();
      this._sizingRaf1 = requestAnimationFrame(() => {
        this._sizingRaf1 = null;
        this._sizingRaf2 = requestAnimationFrame(() => {
          this._sizingRaf2 = null;
          this.applyFramedCardSizingForMode();
        });
      });
      this._sizingTimerShort = setTimeout(() => {
        this._sizingTimerShort = null;
        this.applyFramedCardSizingForMode();
      }, 120);
      this._sizingTimerLong = setTimeout(() => {
        this._sizingTimerLong = null;
        this.applyFramedCardSizingForMode();
      }, 420);
    }

    bindStageHoverHandlers() {
      this.stages = Array.from(this.root.querySelectorAll('.collection-framed-card__stage'));
      this.stages.forEach((stage) => {
        stage.addEventListener('pointerenter', this.onStagePointerEnter);
        stage.addEventListener('pointerleave', this.onStagePointerLeave);
        stage.addEventListener('mouseenter', this.onStageMouseEnter);
        stage.addEventListener('mouseleave', this.onStageMouseLeave);
      });
    }

    applyFramedCardSizingForMode() {
      const columns = this.getCurrentGridColumns();
      const framedCards = Array.from(this.root.querySelectorAll('.collection-framed-card'));
      framedCards.forEach((card) => {
        const image = card.querySelector('.collection-framed-card__image');
        if (!image) return;
        card.style.removeProperty('--collection-image-scale');
        card.style.removeProperty('--collection-image-hover-scale');
        card.style.removeProperty('--collection-frame-scale');
        card.style.removeProperty('--collection-frame-hover-scale');

        const inlineStyle = (card.getAttribute('style') || '').toLowerCase();
        const isHorizontalCard =
          card.classList.contains('collection-framed-card--horizontal') ||
          inlineStyle.includes('horizontal_black_border_collector.webp') ||
          inlineStyle.includes('horizontal_white_border_collector.webp') ||
          inlineStyle.includes('horizontal_natural_wood_border_collector.webp') ||
          inlineStyle.includes('horizontal_wood_border_collector.webp');
        const isHorizontalThreeTwoCard =
          card.classList.contains('collection-framed-card--horizontal-3-2') ||
          inlineStyle.includes('horizontal-3-2_black_border_collector.webp') ||
          inlineStyle.includes('horizontal-3-2_white_border_collector.webp') ||
          inlineStyle.includes('horizontal-3-2_natural_wood_border_collector.webp') ||
          inlineStyle.includes('horizontal-3-2_wood_border_collector.webp');
        const isVerticalCard =
          card.classList.contains('collection-framed-card--vertical') ||
          card.classList.contains('collection-framed-card--vertical-2-3') ||
          inlineStyle.includes('vertical_black_border_collector.webp') ||
          inlineStyle.includes('vertical_white_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_black_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_white_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_natural_wood_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_wood_border_collector.webp') ||
          inlineStyle.includes('vertical_natural_wood_border_collector.webp') ||
          inlineStyle.includes('vertical_wood_border_collector.webp');
        const isVerticalTwoThreeCard =
          card.classList.contains('collection-framed-card--vertical-2-3') ||
          inlineStyle.includes('vertical-2-3_black_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_white_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_natural_wood_border_collector.webp') ||
          inlineStyle.includes('vertical-2-3_wood_border_collector.webp');
        const isSquareCard =
          card.classList.contains('collection-framed-card--square') ||
          inlineStyle.includes('square_black_border_collector.webp') ||
          inlineStyle.includes('square_white_border_collector.webp') ||
          inlineStyle.includes('square_natural_wood_border_collector.webp');
        const widthAttr = Number(image.getAttribute('width') || 0);
        const heightAttr = Number(image.getAttribute('height') || 0);
        const imageRatio = widthAttr > 0 && heightAttr > 0 ? widthAttr / heightAttr : 0;
        const isThreeTwoByMediaRatio = imageRatio >= 1.45 && imageRatio <= 1.55;
        const isHorizontalThreeTwoEffective = isHorizontalThreeTwoCard || (isHorizontalCard && isThreeTwoByMediaRatio);

        if (columns === 6 && isHorizontalThreeTwoEffective) {
          card.style.setProperty('--collection-image-scale', '1.03');
          card.style.setProperty('--collection-image-hover-scale', '1.06');
          card.style.setProperty('--collection-frame-scale', '0.83');
          card.style.setProperty('--collection-frame-hover-scale', '0.88');
          image.style.setProperty('width', '68%', 'important');
          image.style.setProperty('height', '49.5%', 'important');
          image.style.setProperty('top', '50%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 3 && isHorizontalThreeTwoEffective) {
          card.style.setProperty('--collection-image-scale', '1.03');
          card.style.setProperty('--collection-image-hover-scale', '1.06');
          card.style.setProperty('--collection-frame-scale', '0.83');
          card.style.setProperty('--collection-frame-hover-scale', '0.88');
          image.style.setProperty('width', '68%', 'important');
          image.style.setProperty('height', '49.5%', 'important');
          image.style.setProperty('top', '50%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 4 && isHorizontalThreeTwoEffective) {
          card.style.setProperty('--collection-image-scale', '1.03');
          card.style.setProperty('--collection-image-hover-scale', '1.06');
          card.style.setProperty('--collection-frame-scale', '0.83');
          card.style.setProperty('--collection-frame-hover-scale', '0.88');
          image.style.setProperty('width', '70.5%', 'important');
          image.style.setProperty('height', '51.3%', 'important');
          image.style.setProperty('top', '50%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 2 && isHorizontalThreeTwoCard) {
          image.style.setProperty('width', '78%', 'important');
          image.style.setProperty('height', '55.8%', 'important');
          image.style.setProperty('top', '50%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 1 && isHorizontalThreeTwoEffective) {
          image.style.setProperty('width', '79%', 'important');
          image.style.setProperty('height', '56.6%', 'important');
          image.style.setProperty('top', '50%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 1 && isHorizontalCard && !isHorizontalThreeTwoEffective) {
          image.style.setProperty('width', '59.8%', 'important');
          image.style.setProperty('height', '42.6%', 'important');
          image.style.setProperty('top', '50.2%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 2 && isHorizontalCard) {
          image.style.setProperty('width', '62%', 'important');
          image.style.setProperty('height', '44%', 'important');
          image.style.setProperty('top', '50%', 'important');
          image.style.setProperty('left', '50%', 'important');
          return;
        }

        if (columns === 2 && isVerticalTwoThreeCard) {
          image.style.setProperty('width', '53.3%', 'important');
          image.style.setProperty('height', '75.6%', 'important');
          image.style.setProperty('top', '50.1%', 'important');
          image.style.setProperty('left', '50.4%', 'important');
          return;
        }

        if (columns === 2 && isVerticalCard) {
          image.style.setProperty('width', '48.8%', 'important');
          image.style.setProperty('height', '69%', 'important');
          image.style.setProperty('top', '49%', 'important');
          image.style.setProperty('left', '50.4%', 'important');
          return;
        }

        if (columns === 1 && isVerticalCard && !isVerticalTwoThreeCard) {
          image.style.setProperty('top', '48.8%', 'important');
          image.style.setProperty('left', '50.7%', 'important');
          return;
        }

        if (columns === 1 && isSquareCard) {
          image.style.setProperty('left', '50.6%', 'important');
          return;
        }

        if (isVerticalTwoThreeCard) {
          if (columns === 1) {
            image.style.setProperty('width', '53.0%', 'important');
            image.style.setProperty('height', '75.7%', 'important');
            image.style.setProperty('top', '49.9%', 'important');
            image.style.setProperty('left', '49.9%', 'important');
            return;
          }

          if (columns === 6) {
            image.style.setProperty('width', '48.8%', 'important');
            image.style.setProperty('height', '69.9%', 'important');
            image.style.setProperty('top', '50.1%', 'important');
            image.style.setProperty('left', '49.8%', 'important');
            return;
          }

          if (columns === 4) {
            image.style.setProperty('width', '49.2%', 'important');
            image.style.setProperty('height', '70.1%', 'important');
            image.style.setProperty('top', '50.0%', 'important');
            image.style.setProperty('left', '49.8%', 'important');
            return;
          }

          if (columns === 3) {
            image.style.setProperty('width', '49.0%', 'important');
            image.style.setProperty('height', '70.0%', 'important');
            image.style.setProperty('top', '49.9%', 'important');
            image.style.setProperty('left', '49.7%', 'important');
            return;
          }
        }

        if (isSquareCard) {
          if (columns === 6) {
            /* 6-up (dense) square previews: slight size bump and right nudge. */
            image.style.setProperty('width', '49%', 'important');
            image.style.setProperty('height', '49%', 'important');
            image.style.setProperty('top', '50%', 'important');
            image.style.setProperty('left', '50.7%', 'important');
            return;
          }

          if (columns === 3) {
            /* 3-up square previews: enlarge and nudge right to align print window. */
            image.style.setProperty('width', '50%', 'important');
            image.style.setProperty('height', '50%', 'important');
            image.style.setProperty('top', '50%', 'important');
            image.style.setProperty('left', '50.8%', 'important');
            return;
          }

          if (columns === 4) {
            /* 4-up square previews: scale up slightly and nudge right to align the print window. */
            image.style.setProperty('width', '54%', 'important');
            image.style.setProperty('height', '54%', 'important');
            image.style.setProperty('top', '50%', 'important');
            image.style.setProperty('left', '50.8%', 'important');
            return;
          }

          /* Keep square cards on a single slot system driven by Liquid/CSS variables. */
          image.style.removeProperty('width');
          image.style.removeProperty('height');
          image.style.removeProperty('top');
          image.style.removeProperty('left');
          return;
        }

        if (columns === 3 && isVerticalCard) {
          image.style.setProperty('width', '46.5%', 'important');
          image.style.setProperty('height', '66%', 'important');
          image.style.setProperty('top', '48%', 'important');
          image.style.setProperty('left', '51%', 'important');
          return;
        }

        image.style.removeProperty('width');
        image.style.removeProperty('height');
        image.style.removeProperty('top');
        image.style.removeProperty('left');
      });
    }

    getCurrentGridColumns() {
      if (!this.grid) return 0;

      const items = Array.from(this.grid.querySelectorAll(':scope > .grid__item'));
      if (!items.length) return 0;

      const gridRect = this.grid.getBoundingClientRect();
      const firstItemRect = items[0].getBoundingClientRect();
      const gridWidth = gridRect.width;
      const itemWidth = firstItemRect.width;
      if (!gridWidth || !itemWidth) return 0;

      const columns = Math.round(gridWidth / itemWidth);

      return columns;
    }

    onWindowResize() {
      this.applyFramedCardSizingForMode();
      this.schedulePostLayoutSizingPasses();
    }

    onStagePointerEnter(event) {
      const stage = event.currentTarget;
      stage.classList.add('is-stage-hovered');
      const image = stage.querySelector('.collection-framed-card__image');
      const overlay = stage.querySelector('.collection-framed-card__overlay');
      if (image) image.style.removeProperty('transform');
      if (overlay) overlay.style.removeProperty('transform');
    }

    onStagePointerLeave(event) {
      const stage = event.currentTarget;
      stage.classList.remove('is-stage-hovered');
      const image = stage.querySelector('.collection-framed-card__image');
      const overlay = stage.querySelector('.collection-framed-card__overlay');
      if (image) image.style.removeProperty('transform');
      if (overlay) overlay.style.removeProperty('transform');
    }

    onStageMouseEnter(event) {
      this.onStagePointerEnter(event);
    }

    onStageMouseLeave(event) {
      this.onStagePointerLeave(event);
    }
  }

  const instances = [];
  let gridContainerObserver;

  const initCollectionGridViews = () => {
    instances.splice(0).forEach((instance) => instance.destroy());
    const roots = new Set();

    document.querySelectorAll('[data-collection-view-toggle]').forEach((toggle) => {
      roots.add(toggle.closest('.section') || document);
    });

    document.querySelectorAll('.hos-featured-collection').forEach((section) => {
      roots.add(section);
    });

    document.querySelectorAll('.hos-product-grid').forEach((section) => {
      roots.add(section);
    });

    roots.forEach((root) => {
      const instance = new CollectionGridView(root);
      instance.init();
      instances.push(instance);
    });
  };

  document.addEventListener('DOMContentLoaded', initCollectionGridViews);
  document.addEventListener('shopify:section:load', initCollectionGridViews);

  const observeGridContainer = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    if (gridContainerObserver) gridContainerObserver.disconnect();
    gridContainerObserver = new MutationObserver(() => {
      initCollectionGridViews();
    });
    gridContainerObserver.observe(container, { childList: true, subtree: true });
  };

  document.addEventListener('DOMContentLoaded', observeGridContainer);
  document.addEventListener('shopify:section:load', observeGridContainer);
})();
